import { STATUS_TYPE } from '../../global/constants';
import { PullRequest, RawFile, RawTask, RawTimeLogDataInterface, TaskLink } from '../interfaces/task';
import { rootStore } from '../rootStore';
import { CommentModel, createObservableComment } from './comment';
import { RawComment } from '../interfaces/comment';
import { ProjectTemplateModel } from './projectTemplate';
import { getRawTaskLinks } from '../../services/taskServices';
import { VercelDeployments } from '../interfaces/project';
import { makeAutoObservable } from 'mobx';

export class TaskModel {
  // Common
  id: string;
  name: string;
  description: string;
  isArchived: boolean;
  createdOn: Date;
  dueDate: Date | null;
  estimate: number | null;
  updatedTime: Date | null;
  createdBy: string;
  updatedBy: string;

  // Relational
  projectId: string;
  milestoneId: string | null;
  groupId: string | null;
  parentTaskId: string | null;
  companyId: string;
  statusId: string;
  taskSubscribers: string[];
  usersIds: string[];

  // Others
  index: number;
  files: RawFile[];
  comments: CommentModel[];
  labelsIds: string[];
  priority: number | null;
  status: boolean;
  subtaskIndex: number | null;
  taskNumber?: number;
  pullRequests: PullRequest[];
  links: TaskLink[] = [];
  timeLogs?: RawTimeLogDataInterface[];
  recurringInterval: string | null;
  start: string | null;
  vercelDeployments: VercelDeployments[] = [];

  constructor(rawTask: RawTask) {
    makeAutoObservable(this);

    // Common
    this.id = rawTask.id;
    this.name = rawTask.name;
    this.description = rawTask.description;
    this.isArchived = rawTask.isArchived;

    // Dates
    this.createdOn = new Date(rawTask.createdOn);
    this.dueDate = rawTask.dueDate ? new Date(rawTask.dueDate) : null;
    this.estimate = rawTask.estimate;
    this.updatedTime = rawTask.updatedTime ? new Date(rawTask.updatedTime) : null;

    // Relational
    this.createdBy = rawTask.createdBy;
    this.updatedBy = rawTask.updatedBy;
    this.companyId = rawTask.companyId;
    this.projectId = rawTask.projectId;
    this.milestoneId = rawTask.milestoneId;
    this.groupId = rawTask.groupId;
    this.parentTaskId = rawTask.parentTaskId;
    this.statusId = rawTask.statusId;
    this.taskSubscribers = rawTask.taskSubscribers;
    this.usersIds = rawTask.users || [];

    // Others
    this.index = rawTask.index || this.createdOn.getTime();
    this.files = rawTask.files;
    this.comments = rawTask.comments.map((comment) => createObservableComment(comment));
    this.labelsIds = rawTask.labels || [];
    this.priority = rawTask.priority ?? null;
    this.status = rawTask.status;
    this.subtaskIndex = rawTask.subtaskIndex ?? this.createdOn.getTime();
    this.taskNumber = rawTask.taskNumber;
    this.pullRequests = rawTask.pullRequests;
    this.timeLogs = rawTask.timeLogs;
    this.recurringInterval = rawTask.recurringInterval;
    this.start = rawTask.start;
    this.links = rawTask.links?.map(getRawTaskLinks) || [];
    this.vercelDeployments = [];
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users.filter((user) => this.usersIds.includes(user.id)) || [];
  }

  get subTasks() {
    return (
      rootStore.taskStore.tasks
        .filter((task) => task.parentTaskId === this.id)
        .sort((a, b) => (a.subtaskIndex ?? 0) - (b.subtaskIndex ?? 0)) ?? []
    );
  }

  get parentTask(): TaskModel | null {
    if (!this.parentTaskId) return null;
    return rootStore.taskStore.tasks.find((task) => task.id === this.parentTaskId) ?? null;
  }

  get project() {
    return rootStore.projectStore.projects.find((project) => project.id === this.projectId) ?? null;
  }

  get labels() {
    return this.project?.labelsList.filter((label) => this.labelsIds.includes(label.id)) ?? [];
  }

  get statusDetails() {
    if (!this.project?.statusEnable) {
      return undefined;
    }

    const statusDetails =
      this.project.statusData.find((status) => status['_id'] === this.statusId) ??
      this.project.statusData.find((status) => status.Default);

    return statusDetails;
  }

  hasAssignee(userId: string) {
    return this.usersIds.includes(userId);
  }

  addAssignee(userId: string, isTemplateProject?: boolean) {
    const project = isTemplateProject ? rootStore.projectTemplateStore.currentProjectTemplate : this.project;
    if (project?.multiAssignee) {
      this.usersIds.push(userId);
    } else {
      this.usersIds = [userId];
    }
  }

  setName(name: string) {
    this.name = name;
  }

  setProjectId(projectId: string) {
    this.projectId = projectId;
  }

  removeAssignee(userId: string) {
    this.usersIds = this.usersIds.filter((id) => id !== userId);
  }

  clearAssignees() {
    this.usersIds = [];
  }

  setStatus(newStatus: string | boolean, changeSubTasks?: boolean, projectTemplate?: ProjectTemplateModel) {
    const isStatusEnabled = projectTemplate ? projectTemplate.statusEnable : this.project?.statusEnable;
    if (isStatusEnabled) {
      if (typeof newStatus !== 'string') return;
      const projectStatusData = projectTemplate ? projectTemplate.statusData || [] : this.project?.statusData || [];
      const newStatusObj = projectStatusData?.find((status) => status['_id'] === newStatus);

      if (!newStatusObj) {
        return;
      }

      if (newStatusObj.Type === STATUS_TYPE.COMPLETED) {
        this.status = true;
        if (changeSubTasks) {
          this.subTasks.forEach((subTask) => {
            subTask.statusId = newStatusObj['_id'];
            subTask.status = true;
          });
        }
      } else {
        if (this.status) {
          this.status = false;
        }
      }
      this.statusId = newStatusObj['_id'];
    } else {
      if (typeof newStatus !== 'boolean') return;
      this.status = newStatus;
      if (newStatus && changeSubTasks) {
        this.subTasks.forEach((subTask) => {
          subTask.status = true;
        });
      }
    }
  }

  setGroup(groupId: string | null) {
    this.groupId = groupId;
  }

  setMilestone(milestoneId: string | null) {
    this.milestoneId = milestoneId;
  }

  setDueDate(dueDate: Date | null) {
    this.dueDate = dueDate ? new Date(dueDate) : null;
  }

  setEstimate(estimate: number) {
    this.estimate = estimate;
  }

  addLabel(labelId: string) {
    this.labelsIds.push(labelId);
  }

  removeLabel(labelId: string) {
    this.labelsIds = this.labelsIds.filter((id) => id !== labelId);
  }

  hasLabel(labelId: string) {
    return this.labelsIds.includes(labelId);
  }

  setDescription(description: string) {
    this.description = description;
  }

  setPriority(priority: number) {
    this.priority = priority;
  }

  setComments(comments: RawComment[]) {
    this.comments = comments.map(createObservableComment);
  }

  upsertComment(comment: RawComment) {
    const existingIndex = this.comments.findIndex((c) => c.id === comment.id);
    if (existingIndex >= 0) {
      // For existing comments, just update in place without modifying array order
      this.comments[existingIndex] = createObservableComment(comment);
    } else {
      // For new comments, add to end
      this.comments.push(createObservableComment(comment));
    }
  }

  removeComment(commentId: string) {
    const comment = this.comments.find((comment) => comment.id === commentId);
    if (comment) {
      comment.isArchived = true;
    }
  }

  setParentTask(parentTaskId: string | null) {
    this.parentTaskId = parentTaskId;
  }

  setIndex(index: number) {
    this.index = index;
  }

  setTimeLogs(timeLogs: RawTimeLogDataInterface[]) {
    this.timeLogs = timeLogs;
  }

  addLink(link: TaskLink) {
    this.links.push(link);
  }

  removeLink(linkId: string) {
    this.links = this.links.filter((link) => link.id !== linkId);
  }

  updateLink(linkId: string, link: TaskLink) {
    const index = this.links.findIndex((link) => link.id === linkId);
    if (index !== -1) {
      this.links[index] = link;
    }
  }

  setFiles(files: RawFile[]) {
    this.files = files;
  }

  setSubtaskIndex(subtaskIndex: number) {
    this.subtaskIndex = subtaskIndex;
  }

  setRecurringInterval(recurringInterval: string) {
    this.recurringInterval = recurringInterval;
  }

  setStart(start: string | null) {
    this.start = start;
  }

  setVercelDeployment(data: VercelDeployments[]) {
    this.vercelDeployments = data;
  }
  // *getGroupName() {
  //   if (!this.groupId) return 'other';
  //   const groups: string = yield fetchGroupName(this.groupId);
  //   return groups;
  // }
}

export const createObservableTask = (rawTask: RawTask) => {
  return new TaskModel(rawTask);
};
